/**
 * Document head management.
 *
 * The site is a client-rendered SPA, so every tag here is written after the page data
 * arrives. That is fine for canonical and hreflang - crawlers that matter execute
 * JavaScript - but it is worth being honest that server rendering would be better, and
 * is the right answer if organic search ever becomes a priority. Noted in the SEO
 * section of docs/milestones.md.
 *
 * Every tag written here is marked `data-weco-head` so the next page can clear exactly
 * what the previous one added, and nothing else.
 */

const MARK = 'data-weco-head';

function clear() {
  document.head.querySelectorAll(`[${MARK}]`).forEach((el) => el.remove());
}

function add(tag: 'link' | 'meta', attrs: Record<string, string>) {
  const el = document.createElement(tag);
  for (const [key, value] of Object.entries(attrs)) el.setAttribute(key, value);
  el.setAttribute(MARK, '');
  document.head.appendChild(el);
}

export interface HeadOptions {
  title: string;
  description?: string | null;
  /** Absolute URL of this page on its own regional domain. */
  canonical?: string;
  /** `{ locale, url }` for the same page in the region's other languages. */
  alternates?: { locale: string; url: string }[];
  ogImage?: string;
  noindex?: boolean;
}

/**
 * Called after every head write.
 *
 * Analytics listens here rather than to the router: a page view sent on the URL change
 * would carry the previous page's title, because the title is only known once the CMS
 * has answered. This fires at the moment the page is actually the page it claims to be.
 */
type HeadListener = (info: { title: string; noindex: boolean }) => void;
const headListeners = new Set<HeadListener>();

export function onHeadApplied(listener: HeadListener): () => void {
  headListeners.add(listener);
  return () => { headListeners.delete(listener); };
}

export function applyHead({ title, description, canonical, alternates, ogImage, noindex }: HeadOptions) {
  document.title = title;
  clear();

  if (description) add('meta', { name: 'description', content: description });
  if (noindex) add('meta', { name: 'robots', content: 'noindex, nofollow' });
  if (canonical) add('link', { rel: 'canonical', href: canonical });

  for (const alt of alternates ?? []) {
    add('link', { rel: 'alternate', hreflang: alt.locale, href: alt.url });
  }

  add('meta', { property: 'og:title', content: title });
  add('meta', { property: 'og:type', content: 'website' });
  if (description) add('meta', { property: 'og:description', content: description });
  if (canonical) add('meta', { property: 'og:url', content: canonical });
  if (ogImage) add('meta', { property: 'og:image', content: ogImage });

  for (const listener of headListeners) listener({ title, noindex: Boolean(noindex) });
}

/**
 * The absolute URL of a path on a region's own domain.
 *
 * In development the domain does not resolve to anything, which is exactly right: a
 * canonical URL must name the production address, not localhost.
 */
export function absoluteUrl(domain: string, path: string): string {
  const host = String(domain || '').replace(/^https?:\/\//, '').replace(/\/$/, '');
  if (!host) return '';
  return `https://${host}${path.startsWith('/') ? path : `/${path}`}`;
}
